/**
 * RECEPTION-OS-BUILD-V1 -- the StatusChip canon's PURE data half.
 *
 * ONE canon, two files by the repo's pure-view idiom: this module owns the
 * status-token -> (label, tone) DATA and the pure resolvers (no React), and
 * StatusChip.tsx stays the ONE render delegate over it. Extracted so pure
 * view modules (patient-360-view.ts, ClinicDayBoard helpers, lib-adjacent
 * tests) can consume the canon without importing a component. StatusChip.tsx
 * re-exports everything here, so every existing import site is unchanged --
 * this is a within-home split, never a second canon (CHIP law).
 *
 * F-09 (founder letter 2026-08-03): the competing appointment-status label
 * maps die by ADOPTING this canon -- ClinicDayBoard.dayStatusChip,
 * owner/Lead360Panel, owner/leakage-funnel-view (fulfilled bucket), and
 * patients/patient-360-view now resolve through it. The lib plane's ONE home
 * is lib/domain/appointment/appointment-status.ts
 * STORED_APPOINTMENT_STATUS_LABELS, vocabulary-pinned equal to this canon
 * (cross-plane drift breaks the pin). The test-journey copy
 * (journey/journey-view.ts) is fence-forbidden here and ROUTED to the
 * row-39 test-journey package.
 *
 * F-12 (MAKE CONFIGURABLE): display labels resolve org-config-first through
 * `appointmentStatusDisplayLabel` -- the org override map rides /me from THE
 * ONE settings reader (`appointment.status_labels`, seeded empty = these
 * defaults). The STORED vocabulary never changes (R-C VOCABULARY LAW).
 */

import type { AcBadgeTone } from './AcBadge';

export type StatusChipDomain =
  | 'appointment'
  | 'visit'
  | 'conversion'
  | 'commercial'
  | 'payment'
  | 'identity'
  | 'followup'
  | 'sla'
  | 'stage'
  | 'journey'
  | 'intake'
  | 'clinical_operational'
  | 'work_reason';

export interface DomainCanon {
  /** Verbatim label copies from the named source module (lockstep-pinned). */
  readonly labels: Readonly<Record<string, string>>;
  readonly tones: Readonly<Record<string, AcBadgeTone>>;
  /** Tone for a known-domain but unknown-token status (always neutral). */
  readonly fallbackTone: AcBadgeTone;
}

const APPOINTMENT_CANON: DomainCanon = {
  labels: {
    waitlist: 'Waitlisted',
    booked: 'Booked',
    confirmed: 'Confirmed',
    rescheduled: 'Rescheduled',
    arrived: 'Arrived',
    checked_in: 'Checked in',
    fulfilled: 'Visit complete',
    completed: 'Visit complete',
    cancelled: 'Cancelled',
    no_show: 'No show',
  },
  tones: {
    waitlist: 'neutral',
    booked: 'info',
    confirmed: 'info',
    rescheduled: 'info',
    arrived: 'brand',
    checked_in: 'brand',
    fulfilled: 'positive',
    completed: 'positive',
    cancelled: 'danger',
    no_show: 'danger',
  },
  fallbackTone: 'neutral',
};

const VISIT_CANON: DomainCanon = {
  labels: {
    no_visit: 'No visit yet',
    not_started: 'Visit not started',
    desk_closed_no_record: 'Closed at the desk - no doctor visit record',
    in_progress: 'In consultation',
    finalized: 'Visit finalized by the doctor',
    unknown_on_this_read: 'Visit state unavailable',
  },
  // No founder-walked chip-tone precedent (the band renders visit as a note,
  // not a chip) -- neutral everywhere until a walked source exists.
  tones: {},
  fallbackTone: 'neutral',
};

const CONVERSION_CANON: DomainCanon = {
  labels: {
    none: 'No outcome yet',
    awaiting_decision: 'Advised - decision pending',
    converted: 'Converted',
    contradiction: 'Conflicting records - needs review',
    // partially_accepted / not_converted labels are contextual (the truth
    // contract resolves counts/wording) -> caller-resolved via `label`.
  },
  tones: {
    none: 'neutral',
    awaiting_decision: 'info',
    converted: 'positive',
    partially_accepted: 'info',
    not_converted: 'danger',
    contradiction: 'danger',
  },
  fallbackTone: 'neutral',
};

const COMMERCIAL_CANON: DomainCanon = {
  labels: {
    no_treatment_recommended: 'No treatment recommended',
    undecided: 'Undecided',
    accepted_payment_pending: 'Accepted - payment pending',
    partially_accepted_payment_pending: 'Partially accepted - payment pending',
    declined: 'Declined',
    deferred: 'Deferred',
    converted_first_payment: 'First payment received - Converted',
    converted_chief_complaint: 'Converted - Chief Complaint',
    converted_full_package: 'Converted - Full Package',
    unavailable: 'Treatment status unavailable',
  },
  tones: {},
  fallbackTone: 'neutral',
};

const PAYMENT_CANON: DomainCanon = {
  labels: {
    recorded: 'Payment recorded',
    none: 'No payment activity',
    advised_only_no_handoff: 'No payment due - advised only',
    unknown_on_this_read: 'Payment state unavailable',
    // collect_pending is contextual (carries the pending amount) ->
    // caller-resolved via `label`.
  },
  tones: {
    recorded: 'positive',
    collect_pending: 'pending',
    advised_only_no_handoff: 'info',
    none: 'neutral',
    unknown_on_this_read: 'neutral',
  },
  fallbackTone: 'neutral',
};

const IDENTITY_CANON: DomainCanon = {
  labels: {
    none: 'No patient record linked',
    possible_match: 'Possible existing patient',
    unknown: 'Patient link unknown',
    // linked is contextual (names the linked record) -> caller-resolved.
  },
  tones: {
    linked: 'positive',
    possible_match: 'brand',
    none: 'neutral',
    unknown: 'neutral',
  },
  fallbackTone: 'neutral',
};

const FOLLOWUP_CANON: DomainCanon = {
  labels: {
    none: 'No follow-up due',
    // required is contextual (the whyLabel) -> caller-resolved.
  },
  tones: {
    required: 'info',
    none: 'neutral',
  },
  fallbackTone: 'neutral',
};

const SLA_CANON: DomainCanon = {
  labels: {
    clear: 'No SLA breach',
    unavailable: 'SLA not available on this read',
    // breach_amber / breach_red are contextual (count + class) ->
    // caller-resolved via `label`.
  },
  tones: {
    clear: 'neutral',
    unavailable: 'neutral',
    breach_amber: 'pending',
    breach_red: 'danger',
  },
  fallbackTone: 'neutral',
};

const STAGE_CANON: DomainCanon = {
  // Stage labels stay owned by the lifecycle rail (see StatusChip JSDoc).
  labels: {},
  // STAGE DISPLAY RULE: the CRM stage chip is always neutral.
  tones: {},
  fallbackTone: 'neutral',
};

const JOURNEY_CANON: DomainCanon = {
  labels: {
    complete: 'Done',
    current: 'Now',
    pending: 'Pending',
    not_evidenced: 'Not evidenced',
    evidenced: 'Evidence recorded',
    unavailable: 'Unavailable',
    not_applicable: 'Not applicable',
  },
  tones: {
    complete: 'positive',
    current: 'brand',
    pending: 'neutral',
    not_evidenced: 'pending',
    evidenced: 'info',
    unavailable: 'neutral',
    not_applicable: 'neutral',
  },
  fallbackTone: 'neutral',
};

const INTAKE_CANON: DomainCanon = {
  labels: {
    none: 'Not started',
    in_progress: 'In progress',
    completed: 'Complete',
  },
  tones: {},
  fallbackTone: 'neutral',
};

/**
 * MY-WORK-PRIMITIVE-CONFORMANCE-V6 -- the call-centre WORK REASON (the
 * "why now" category of a queue row). Labels come from the wire
 * (workReasonLabel); this canon fixes the TONE per category so the same
 * category shows the same hue on every queue (the founder-approved visual
 * reference: missed = danger, follow-up = pending, confirm / callback =
 * info, new lead / waiting = neutral, an exception = danger).
 */
const WORK_REASON_CANON: DomainCanon = {
  labels: {},
  tones: {
    missed_appointment: 'danger',
    follow_up: 'pending',
    follow_up_due: 'pending',
    appointment_confirmation: 'info',
    callback: 'info',
    new_lead: 'neutral',
    waiting_response: 'neutral',
    routing_exception: 'danger',
    escalation: 'danger',
  },
  fallbackTone: 'neutral',
};

const CLINICAL_OPERATIONAL_CANON: DomainCanon = {
  // Role-safe OPERATIONAL state only -- never clinical content (the wire
  // fence keeps clinical bodies off non-doctor reads entirely).
  labels: {
    not_started: 'Consultation not started',
    in_progress: 'Consultation in progress',
    completed: 'Clinical record completed',
    unavailable: 'Clinical status unavailable',
  },
  tones: {},
  fallbackTone: 'neutral',
};

export const STATUS_CANON: Readonly<Record<StatusChipDomain, DomainCanon>> = {
  appointment: APPOINTMENT_CANON,
  visit: VISIT_CANON,
  conversion: CONVERSION_CANON,
  commercial: COMMERCIAL_CANON,
  payment: PAYMENT_CANON,
  identity: IDENTITY_CANON,
  followup: FOLLOWUP_CANON,
  sla: SLA_CANON,
  stage: STAGE_CANON,
  journey: JOURNEY_CANON,
  intake: INTAKE_CANON,
  clinical_operational: CLINICAL_OPERATIONAL_CANON,
  work_reason: WORK_REASON_CANON,
};

/** Readable de-cased fallback for an unknown token ('no_show' -> 'No show')
 *  -- honest sentence case, never a raw token echo. */
export function statusFallbackLabel(status: string): string {
  const words = status.split('_').filter((part) => part.length > 0);
  if (words.length === 0) return 'Unknown';
  const joined = words.join(' ');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

/** THE canon tone for a domain/status (neutral for unknown tokens). */
export function statusChipTone(
  domain: StatusChipDomain,
  status: string,
): AcBadgeTone {
  const canon = STATUS_CANON[domain];
  return canon.tones[status] ?? canon.fallbackTone;
}

/** THE canon label for a domain/status. Precedence: explicit caller label
 *  (contextual statuses) -> canon label -> de-cased fallback. */
export function statusChipLabel(
  domain: StatusChipDomain,
  status: string,
  explicitLabel?: string | null,
): string {
  if (explicitLabel !== undefined && explicitLabel !== null && explicitLabel !== '') {
    return explicitLabel;
  }
  return STATUS_CANON[domain].labels[status] ?? statusFallbackLabel(status);
}

/** Org-config override map for appointment-status DISPLAY labels (the
 *  `appointment.status_labels` key, served on /me). Partial by design. */
export type AppointmentStatusLabelOverrides = Readonly<Record<string, string>>;

/**
 * THE appointment-status display label (F-09 one owner + F-12 config half):
 * org override first, then the canon, then the honest de-cased fallback.
 * The STORED token is never changed by any value here (VOCABULARY LAW).
 */
export function appointmentStatusDisplayLabel(
  status: string,
  orgLabels?: AppointmentStatusLabelOverrides | null,
): string {
  const override = orgLabels?.[status];
  if (typeof override === 'string' && override.trim() !== '') {
    return override;
  }
  return statusChipLabel('appointment', status);
}

/** THE appointment-status chip tone (CHIP law; the canon is the one owner). */
export function appointmentStatusDisplayTone(status: string): AcBadgeTone {
  return statusChipTone('appointment', status);
}
